import { useState, useEffect, useMemo } from "react";
import { AlertTriangle, X, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

/**
 * Gate 3e — Renewal Grace-Period Banner.
 *
 * Shows a yellow countdown banner at the top of every page when the user's
 * paid subscription is within the next 30 days of expiry. Urgency escalates:
 *   • 30-15 days  → amber, polite ("30 days until renewal")
 *   • 14-8 days   → orange, gentle push
 *   • 7-4 days    → red, prominent
 *   • 3-1 days    → red + pulse animation, urgent
 *   • 0 / expired → handled by existing expiry paywall (this banner hides)
 *
 * Per-session dismissal — clicking X stores a sessionStorage flag so the banner
 * stays hidden until the user closes the browser. Reappears on next session
 * unless they renew.
 *
 * Owner/signature/sponsored accounts NEVER see this banner (license.type !== "paid").
 */
const DISMISS_KEY = "renewal_grace_dismissed_until";

function parseLicenseExpiry(license) {
  if (!license?.license_expires_at) return null;
  try {
    const dt = new Date(license.license_expires_at);
    if (Number.isFinite(dt.getTime())) return dt;
  } catch {/* fallthrough */}
  return null;
}

export default function RenewalGraceBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(() => {
    try {
      const until = sessionStorage.getItem(DISMISS_KEY);
      if (!until) return false;
      return Date.now() < parseInt(until, 10);
    } catch { return false; }
  });

  const banner = useMemo(() => {
    if (!user) return null;
    const license = user.license;
    if (!license) return null;
    // Only paid users see this banner — owner/signature/sponsored never do
    if (license.type !== "paid") return null;
    if (license.status !== "active") return null;
    const expiry = parseLicenseExpiry(license);
    if (!expiry) return null;
    const now = new Date();
    const ms = expiry.getTime() - now.getTime();
    const days = Math.ceil(ms / (24 * 60 * 60 * 1000));
    if (days <= 0 || days > 30) return null;

    let level = "amber";
    let pulse = false;
    if (days <= 3) { level = "red"; pulse = true; }
    else if (days <= 7) { level = "red"; }
    else if (days <= 14) { level = "orange"; }

    const styles = {
      amber: "bg-amber-50 border-amber-300 text-amber-900",
      orange: "bg-orange-50 border-orange-300 text-orange-900",
      red: "bg-red-50 border-red-300 text-red-900",
    }[level];

    return {
      days,
      level,
      pulse,
      expiry,
      styles,
      label: days === 1 ? "1 day" : `${days} days`,
    };
  }, [user]);

  if (!banner || hidden) return null;

  const dismiss = () => {
    setHidden(true);
    try {
      // Re-show after 4 hours so urgent reminders come back even within the same session.
      sessionStorage.setItem(DISMISS_KEY, String(Date.now() + 4 * 3600 * 1000));
    } catch {/* silent */}
  };

  return (
    <div
      className={`border-b ${banner.styles} ${banner.pulse ? "animate-pulse" : ""}`}
      data-testid="renewal-grace-banner"
      data-urgency={banner.level}
      data-days-remaining={banner.days}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-4 py-2 text-sm">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">
            <strong>{banner.label}</strong> until your Tax Year Access renews on{" "}
            <strong>{banner.expiry.toLocaleDateString()}</strong>.
            {banner.days <= 7 && " Renew now to keep your access without interruption."}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate("/app/pricing")}
            className={`text-xs font-medium px-3 py-1 rounded-full inline-flex items-center gap-1 ${
              banner.level === "red" ? "bg-red-600 hover:bg-red-700 text-white" :
              banner.level === "orange" ? "bg-orange-600 hover:bg-orange-700 text-white" :
              "bg-amber-600 hover:bg-amber-700 text-white"
            }`}
            data-testid="renewal-grace-renew-btn"
          >
            <CreditCard className="w-3 h-3" /> Renew
          </button>
          <button
            onClick={dismiss}
            className="text-current opacity-60 hover:opacity-100"
            aria-label="Dismiss for 4 hours"
            data-testid="renewal-grace-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
